import React, { useCallback } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import * as Yup from 'yup';
import { Theme } from '@app/theme';
import { Form, Formik, FormikHelpers } from 'formik';
import {
  AccountFields,
  accountInitialValues,
  accountSchemaFields,
} from './AccountSignup';
import { Trans, useTranslation } from 'react-i18next';
import {
  Checkbox,
  ConsentCheckbox,
  FormikError,
  Input,
  PhoneNumberInput,
  PromoCodeInput,
  YupValidators,
} from '@app/form';
import {
  Account,
  ApiFormattedException,
  Consent,
  useAcceptConsent,
  useLogin,
} from '@app/api';
import { Button, Captcha, SubmitButton } from '@app/core';
import { useCreateClient } from 'queries';
import { useTrackSignUp } from '@app/analytics';

const useStyles = createUseStyles((theme: Theme) => ({
  buttonsLine: {
    display: 'flex',
    alignItems: 'center',
    marginTop: theme.marginBase * 2,
  },
  spacer: {
    flex: 1,
  },
  captcha: {
    marginBottom: theme.marginBase * 2,
  },
}));

interface Props {
  onLoginClick?: () => any;
  onSuccess?: (account: Account) => any;
  trackContext: 'organic' | 'session' | 'checkout';
  noPromoCode?: boolean;
  billingMethod?: string | undefined;
}

const initialValues = {
  account: accountInitialValues,
  organization: {
    name: '',
    billingMethod: '',
  },
  phoneNumber: '',
  jobTitle: '',
  consents: [] as Consent[],
  promoCode: '',
  altcha: '',
};
type Values = typeof initialValues;

const accountSchema = Yup.object().shape({
  account: Yup.object().shape(accountSchemaFields),
  newsletterOptin: Yup.boolean().nullable(),
  phoneNumber: YupValidators.isPhoneNumber(),
  jobTitle: Yup.string().required(),
  organization: Yup.object().shape({
    name: Yup.string().required(),
    billingMethod: Yup.string().nullable(),
  }),
  consents: Yup.array().required().min(1),
});

export const SignupContent = ({
  onLoginClick,
  onSuccess,
  trackContext,
  noPromoCode,
  billingMethod,
}: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const { t } = useTranslation();

  const { mutateAsync: signup } = useCreateClient();
  const { mutateAsync: login } = useLogin();
  const { mutate: addConsentToAccount } = useAcceptConsent();
  const trackSignup = useTrackSignUp();

  const onSubmit = useCallback(
    async (values: Values, helpers: FormikHelpers<Values>) => {
      try {
        const {
          consents,
          account: { passwordRepeat, ...account },
          ...client
        } = values;

        await signup({
          ...client,
          promoCode: values.promoCode,
          organization: {
            ...values.organization,
            billingMethod: billingMethod ?? '',
          },
          account,
        });

        const res = await login({
          email: account.email,
          password: account.password,
          altcha: values.altcha,
        });

        trackSignup({
          ...res,
          phoneNumber: values.phoneNumber as string,
          type: trackContext,
          role: 'client',
          jobTitle: values.jobTitle,
          organizationId: res.organizations?.[0].organizationId,
          organizationName: values.organization.name,
        });

        for (const consent of consents) {
          addConsentToAccount(consent);
        }

        if (onSuccess) {
          onSuccess(res);
        }
      } catch (e) {
        if (
          e instanceof ApiFormattedException &&
          e.getCode() === 'EMAIL_ALREADY_EXISTS'
        ) {
          helpers.setStatus({
            account: { email: t('form.errors.email.alreadyUsed') },
          });
        } else {
          helpers.setStatus(e);
        }
      }
      helpers.setSubmitting(false);
    },
    [
      onSuccess,
      signup,
      login,
      addConsentToAccount,
      trackContext,
      trackSignup,
      t,
    ],
  );

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={accountSchema}
      onSubmit={onSubmit}
    >
      <Form>
        <AccountFields namePrefix="account" />

        <Input
          name="organization.name"
          label={t('form.organization.name')}
          placeholder="Ex: Apple"
        />

        <PhoneNumberInput
          name="phoneNumber"
          label={t('form.client.phoneNumber')}
        />

        <Input
          name="jobTitle"
          label={t('form.participant.jobTitle')}
          placeholder={t('form.participant.placeholder.jobTitle')}
        />

        {!noPromoCode && <PromoCodeInput />}

        <ConsentCheckbox name="consents" consent="CLIENT_SIGNUP" />
        <Checkbox
          name="newsletterOptin"
          label={t('signup.client.newsletter')}
        />
        <Captcha className={classes.captcha} invisible />

        <FormikError />

        <div className={classes.buttonsLine}>
          {onLoginClick && (
            <Button variant="transparent" onClick={onLoginClick}>
              <Trans i18nKey="signup.client.alreadyHaveAccount" />
            </Button>
          )}
          <div className={classes.spacer} />
          <SubmitButton trackingName="signup" id="signup_button">
            <Trans i18nKey="signup.client.submit" />
          </SubmitButton>
        </div>
      </Form>
    </Formik>
  );
};
